import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

/**
 * Custom hook for pagination with URL sync
 * Standardizes pagination behavior across all tables in the system
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.defaultItemsPerPage - Default items per page (default: 25)
 * @param {Array} options.allowedItemsPerPage - Allowed items per page values (default: [10, 25, 50, 100])
 * @param {boolean} options.scrollToTop - Whether to scroll to top on page change (default: true)
 * @param {number} options.scrollOffset - Offset from top when scrolling (default: 0)
 * 
 * @returns {Object} Pagination state and handlers
 */
export const usePaginationWithURL = (options = {}) => {
  const {
    defaultItemsPerPage = 25,
    allowedItemsPerPage = [10, 25, 50, 100],
    scrollToTop = true,
    scrollOffset = 0
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get current page from URL or default to 1
  const getCurrentPageFromURL = useCallback(() => {
    const pageParam = searchParams.get('p');
    const page = pageParam ? parseInt(pageParam, 10) : 1;
    return page > 0 ? page : 1;
  }, [searchParams]);

  // Get items per page from URL or default to defaultItemsPerPage
  const getItemsPerPageFromURL = useCallback(() => {
    const itemsParam = searchParams.get('items');
    const items = itemsParam ? parseInt(itemsParam, 10) : defaultItemsPerPage;
    return allowedItemsPerPage.includes(items) ? items : defaultItemsPerPage;
  }, [searchParams, defaultItemsPerPage, allowedItemsPerPage]);

  const [currentPage, setCurrentPage] = useState(getCurrentPageFromURL);
  const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPageFromURL);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Update URL with current page and items per page
  const updateURL = useCallback((page, items) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (page > 1) {
      newSearchParams.set('p', page.toString());
    } else {
      newSearchParams.delete('p');
    }
    
    if (items !== defaultItemsPerPage) {
      newSearchParams.set('items', items.toString());
    } else {
      newSearchParams.delete('items');
    }
    
    // Convert URLSearchParams to string and remove leading '?' if empty
    const paramString = newSearchParams.toString();
    const newURL = paramString ? `${location.pathname}?${paramString}` : location.pathname;
    
    // Use replace to avoid adding to browser history for pagination
    navigate(newURL, { replace: true });
  }, [searchParams, location.pathname, navigate, defaultItemsPerPage]);

  // Handle page change with optional scroll to top
  const handlePageChange = useCallback((newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      updateURL(newPage, itemsPerPage);
      
      if (scrollToTop) {
        window.scrollTo({
          top: scrollOffset,
          left: 0,
          behavior: 'smooth'
        });
      }
    }
  }, [totalPages, itemsPerPage, updateURL, scrollToTop, scrollOffset]);

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
    updateURL(1, newItemsPerPage);
    
    if (scrollToTop) {
      window.scrollTo({
        top: scrollOffset,
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [updateURL, scrollToTop, scrollOffset]);

  // Sync state with URL changes (browser back/forward)
  useEffect(() => {
    const urlPage = getCurrentPageFromURL();
    const urlItems = getItemsPerPageFromURL();
    
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
    
    if (urlItems !== itemsPerPage) {
      setItemsPerPage(urlItems);
    }
  }, [searchParams, getCurrentPageFromURL, getItemsPerPageFromURL, currentPage, itemsPerPage]);

  // Initialize URL on first load if needed
  useEffect(() => {
    const hasPageParam = searchParams.has('p');
    const hasItemsParam = searchParams.has('items');
    
    // If no URL params and we're not on page 1 or default items, update URL
    if (!hasPageParam && !hasItemsParam && (currentPage !== 1 || itemsPerPage !== defaultItemsPerPage)) {
      updateURL(currentPage, itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only URL sync
  }, []);

  // Calculate total pages when total count changes
  useEffect(() => {
    setTotalPages(Math.ceil(totalCount / itemsPerPage));
  }, [totalCount, itemsPerPage]);

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      handlePageChange(1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  return {
    // State
    currentPage,
    itemsPerPage,
    totalPages,
    totalCount,
    
    // Setters (for external use)
    setTotalCount,
    setTotalPages, // Normally calculated automatically, but available for manual override
    
    // Handlers
    handlePageChange,
    handleItemsPerPageChange,
    
    // Utilities
    updateURL,
    getCurrentPageFromURL,
    getItemsPerPageFromURL,
    
    // Configuration
    allowedItemsPerPage,
    defaultItemsPerPage
  };
};

export default usePaginationWithURL;
